
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import BlogLayout from "@/components/blog/BlogLayout";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { BlogPostSkeleton } from "@/components/blog/BlogPostSkeleton";
import { BlogPagination } from "@/components/blog/BlogPagination";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { ContentLoader } from "@/components/ContentLoader";

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || "1");
  const pageSize = 9;

  const { data, isLoading, error } = useBlogPosts({
    page: currentPage,
    pageSize: pageSize,
    onlyPublished: true
  });

  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page.toString());
      return newParams;
    });
    
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // If URL has invalid page number, redirect to page 1
  useEffect(() => {
    if (data && currentPage > data.pagination.totalPages && data.pagination.totalPages > 0) {
      handlePageChange(1);
    }
  }, [data, currentPage]);

  return (
    <BlogLayout 
      title="Our Blog" 
      description="Explore our latest articles, updates, and insights"
    >
      <div className="w-full max-w-7xl mx-auto px-4 py-8">
        <ContentLoader isLoading={isLoading} error={error instanceof Error ? error : null}>
          {!data || data.data.length === 0 ? (
            <div className="p-6 bg-slate-100 text-slate-600 rounded-lg">
              No blog posts available at the moment.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {data.data.map((post, index) => (
                  <BlogPostCard key={post.id} post={post} index={index} />
                ))}
              </div>
              
              <div className="flex justify-center mt-8">
                <BlogPagination 
                  currentPage={currentPage}
                  totalPages={data.pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
              
              <div className="text-center text-sm text-slate-500 mt-4">
                Showing page {currentPage} of {data.pagination.totalPages} ({data.pagination.total} total posts)
              </div>
            </>
          )}
        </ContentLoader>
        
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(pageSize)].map((_, index) => (
              <BlogPostSkeleton key={index} />
            ))}
          </div>
        )}
      </div>
    </BlogLayout>
  );
};

export default Blog;
